import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const FeesDistributionChart = ({ data, loading }) => {
  const mockData = [
    { name: 'Window Display', value: 1250000, percentage: 28.5, color: '#1E40AF' },
    { name: 'Billboard', value: 980000, percentage: 22.3, color: '#F59E0B' },
    { name: 'LED Screen', value: 850000, percentage: 19.4, color: '#059669' },
    { name: 'In-Store Static', value: 620000, percentage: 14.1, color: '#DC2626' },
    { name: 'Gondola Branding', value: 420000, percentage: 9.6, color: '#7C3AED' },
    { name: 'Brand Zone', value: 180000, percentage: 4.1, color: '#EC4899' },
    { name: 'Others', value: 90000, percentage: 2.0, color: '#64748B' }
  ];

  const chartData = data || mockData;
  const COLORS = chartData?.map(item => item?.color);

  const formatCurrency = (value) => {
    return `৳${(value / 1000)?.toFixed(0)}K`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-surface border border-border rounded-lg p-3 shadow-elevated">
          <div className="flex items-center gap-2 mb-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: data?.color }}
            />
            <span className="font-medium text-foreground">{data?.name}</span>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Amount:</span>
              <span className="font-medium text-foreground">{formatCurrency(data?.value)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Percentage:</span>
              <span className="font-medium text-foreground">{data?.percentage}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percentage }) => {
    if (percentage < 5) return null; // Don't show labels for small slices
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="500"
      >
        {`${percentage}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-80">
          <div className="flex items-center gap-3 text-muted-foreground">
            <div className="animate-spin">
              <Icon name="Loader2" size={20} />
            </div>
            <span>Loading fees distribution...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Revenue Distribution by Type</h3>
          <p className="text-sm text-muted-foreground">Breakdown of branding fees by category</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-foreground">
            ৳{(chartData?.reduce((sum, item) => sum + item?.value, 0) / 100000)?.toFixed(1)}L
          </div>
          <div className="text-sm text-muted-foreground">Total Fees</div>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row items-center gap-6">
        <div className="h-80 w-full lg:w-1/2">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={CustomLabel}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
                stroke="var(--color-background)"
                strokeWidth={2}
              >
                {chartData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="w-full lg:w-1/2 space-y-3">
          {chartData?.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: item?.color }}
                />
                <div>
                  <div className="font-medium text-foreground text-sm">{item?.name}</div>
                  <div className="text-xs text-muted-foreground">{item?.percentage}% of total</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-foreground">{formatCurrency(item?.value)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeesDistributionChart;